import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { DatePicker } from "../components/ui/date-picker";
import { 
  FileText,
  Plus,
  ArrowLeft,
  User,
  Calendar,
  Phone,
  Mail,
  Check,
  X,
  Trash2,
  Download,
  Save,
  ChevronDown,
  ChevronRight,
  Building2,
  Shield,
  DollarSign,
  Sparkles,
  Link2,
  Share2,
  Copy,
  ExternalLink,
  CheckCircle2
} from "lucide-react";
import { authGet, authPost, authPut, getAuthToken } from "../utils/auth";
import jsPDF from "jspdf";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Premium terms
const PREMIUM_TERMS = [
  { id: "annual", label: "Annual (1 Year)", multiplier: 1 },
  { id: "2year", label: "2 Years", multiplier: 2 },
  { id: "3year", label: "3 Years", multiplier: 3 },
  { id: "5year", label: "5 Years", multiplier: 5 }
];

// Sum Insured options
const SI_OPTIONS = ["3L", "5L", "7.5L", "10L", "15L", "20L", "25L", "50L", "75L", "1Cr"];

export default function QuoteGenerator() {
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const isEditing = !!quoteId;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [expandedPlans, setExpandedPlans] = useState({});
  const [savedQuoteId, setSavedQuoteId] = useState(quoteId || null);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Quote data
  const [clientName, setClientName] = useState("");
  const [clientAge, setClientAge] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [quoteDate, setQuoteDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [selectedPlans, setSelectedPlans] = useState([]);

  // Generate shareable link
  const getShareableLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/quote/view/${savedQuoteId}`;
  };

  const copyLinkToClipboard = async () => {
    const link = getShareableLink();
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    }
  };

  const openShareableLink = () => {
    window.open(getShareableLink(), "_blank");
  };

  // Check auth and fetch data
  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate("/advisor/login");
      return;
    }
    fetchAvailablePlans();
    if (isEditing) {
      fetchQuote();
    } else {
      setLoading(false);
    }
  }, [navigate, quoteId, isEditing]);

  const fetchAvailablePlans = async () => {
    try {
      const res = await authGet("/api/advisor/quotes/plans/available");
      if (res.ok) {
        setAvailablePlans(res.data.plans || []);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const res = await authGet(`/api/advisor/quotes/${quoteId}`);
      if (res.ok) {
        const quote = res.data.quote;
        setClientName(quote.client_name || "");
        setClientAge(quote.client_age || "");
        setClientEmail(quote.client_email || "");
        setClientPhone(quote.client_phone || "");
        setQuoteDate(quote.quote_date || new Date().toISOString().split("T")[0]);
        setNotes(quote.notes || "");
        setSelectedPlans(quote.selected_plans || []);
      }
    } catch (error) {
      console.error("Error fetching quote:", error);
    } finally {
      setLoading(false);
    }
  };

  const addPlan = (plan) => {
    // Check if already added
    if (selectedPlans.find(p => p.plan_id === plan.id)) {
      return;
    }
    
    const newPlan = {
      plan_id: plan.id,
      plan_name: plan.name,
      company: plan.company,
      company_id: plan.company_id,
      company_logo: plan.company_logo,
      features: plan.features,
      available_riders: plan.riders || [],
      // Default selections
      selected_si_options: ["10L", "25L"],
      selected_riders: [],
      premiums: {} // { "10L": { "annual": "", "2year": "", "3year": "", "5year": "" }, ... }
    };
    
    setSelectedPlans([...selectedPlans, newPlan]);
    setShowPlanSelector(false);
    // Expand the newly added plan
    setExpandedPlans(prev => ({ ...prev, [plan.id]: true }));
  };

  const removePlan = (planId) => {
    setSelectedPlans(selectedPlans.filter(p => p.plan_id !== planId));
  };

  const toggleSIOption = (planId, si) => {
    setSelectedPlans(selectedPlans.map(p => {
      if (p.plan_id === planId) {
        const currentSI = p.selected_si_options || [];
        if (currentSI.includes(si)) {
          return { ...p, selected_si_options: currentSI.filter(s => s !== si) };
        } else {
          return { ...p, selected_si_options: [...currentSI, si] };
        }
      }
      return p;
    }));
  };

  const toggleRider = (planId, riderName) => {
    setSelectedPlans(selectedPlans.map(p => {
      if (p.plan_id === planId) {
        const currentRiders = p.selected_riders || [];
        if (currentRiders.includes(riderName)) {
          return { ...p, selected_riders: currentRiders.filter(r => r !== riderName) };
        } else {
          return { ...p, selected_riders: [...currentRiders, riderName] };
        }
      }
      return p;
    }));
  };

  const updatePremium = (planId, si, term, value) => {
    setSelectedPlans(selectedPlans.map(p => {
      if (p.plan_id === planId) {
        const premiums = { ...p.premiums };
        if (!premiums[si]) premiums[si] = {};
        premiums[si][term] = value;
        return { ...p, premiums };
      }
      return p;
    }));
  };

  const saveQuote = async (status = "draft") => {
    if (!clientName) {
      alert("Please enter client name");
      return;
    }
    if (selectedPlans.length === 0) {
      alert("Please add at least one plan");
      return;
    }

    setSaving(true);
    try {
      const quoteData = {
        client_name: clientName,
        client_age: clientAge,
        client_email: clientEmail,
        client_phone: clientPhone,
        quote_date: quoteDate,
        selected_plans: selectedPlans,
        notes,
        status
      };

      let res;
      if (isEditing || savedQuoteId) {
        res = await authPut(`/api/advisor/quotes/${savedQuoteId || quoteId}`, quoteData);
      } else {
        res = await authPost("/api/advisor/quotes", quoteData);
      }

      if (res.ok) {
        const newQuoteId = res.data.quote_id || res.data.quote?.id || savedQuoteId;
        setSavedQuoteId(newQuoteId);
        alert(isEditing || savedQuoteId ? "Quote updated!" : "Quote saved! You can now share the link.");
        if (!isEditing && !savedQuoteId) {
          // Update URL without navigating away
          window.history.replaceState(null, "", `/advisor/quotes/${newQuoteId}`);
        }
      }
    } catch (error) {
      console.error("Error saving quote:", error);
      alert("Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  // Save and Share - saves the quote first then copies link
  const saveAndShare = async () => {
    if (!clientName) {
      alert("Please enter client name");
      return;
    }
    if (selectedPlans.length === 0) {
      alert("Please add at least one plan");
      return;
    }

    setSaving(true);
    try {
      const quoteData = {
        client_name: clientName,
        client_age: clientAge,
        client_email: clientEmail,
        client_phone: clientPhone,
        quote_date: quoteDate,
        selected_plans: selectedPlans,
        notes,
        status: "sent"
      };

      let res;
      if (savedQuoteId) {
        res = await authPut(`/api/advisor/quotes/${savedQuoteId}`, quoteData);
      } else {
        res = await authPost("/api/advisor/quotes", quoteData);
      }

      if (res.ok) {
        const newQuoteId = res.data.quote_id || res.data.quote?.id || savedQuoteId;
        setSavedQuoteId(newQuoteId);
        
        // Copy link after save
        const baseUrl = window.location.origin;
        const link = `${baseUrl}/quote/view/${newQuoteId}`;
        
        try {
          await navigator.clipboard.writeText(link);
          setLinkCopied(true);
          setTimeout(() => setLinkCopied(false), 3000);
          alert(`Quote saved! Link copied to clipboard:\n${link}`);
        } catch (err) {
          alert(`Quote saved! Share this link:\n${link}`);
        }
        
        // Update URL
        window.history.replaceState(null, "", `/advisor/quotes/${newQuoteId}`);
      }
    } catch (error) {
      console.error("Error saving quote:", error);
      alert("Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  const generatePDF = () => {
    if (selectedPlans.length === 0) {
      alert("Please add at least one plan");
      return;
    }

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    let yPos = 0;
    let pageNum = 1;

    // Professional color palette
    const colors = {
      white: [255, 255, 255],
      lightGray: [248, 250, 252],
      headerBg: [30, 64, 175],
      sectionBg: [241, 245, 249],
      accentGold: [217, 119, 6],
      textDark: [15, 23, 42],
      textMedium: [71, 85, 105],
      textLight: [148, 163, 184],
      positive: [22, 163, 74],
      negative: [220, 38, 38],
      neutral: [59, 130, 246],
      plan1: [37, 99, 235],
      plan2: [22, 163, 74],
      plan3: [168, 85, 247],
      plan4: [234, 88, 12],
      plan5: [236, 72, 153]
    };

    // Feature explanations for clarity
    const featureExplanations = {
      room_rent: "Hospital room charges coverage. 'No capping' means ANY room is covered without extra cost.",
      icu_charges: "Intensive Care Unit charges for critical patients requiring continuous monitoring.",
      pre_hospitalization: "Medical expenses BEFORE hospital admission (tests, doctor consultations, medicines).",
      post_hospitalization: "Medical expenses AFTER discharge (follow-up visits, medicines, tests).",
      day_care: "Treatments NOT requiring 24-hour stay (dialysis, chemotherapy, cataract surgery, etc.).",
      modern_treatments: "Advanced treatments like robotic surgery, stem cell therapy, gene therapy.",
      organ_donor: "Coverage for the organ donor's surgery and recovery expenses.",
      domiciliary: "Treatment at home when hospitalization is not possible or advisable.",
      ped_waiting: "Waiting period for Pre-Existing Diseases (diabetes, BP, thyroid). SHORTER is BETTER.",
      specific_disease_waiting: "Waiting for specific illnesses (hernia, piles, cataract, knee replacement).",
      copay: "Percentage YOU pay from pocket. NIL/0% is BEST - insurer covers 100%.",
      restore_benefit: "Sum Insured gets restored after a claim - VERY important for families.",
      ncb: "No Claim Bonus - your coverage INCREASES each year if you don't claim. Higher % is better.",
      consumables: "Items used during treatment (gloves, syringes, PPE) - can add Rs.20,000+ to bills.",
      health_checkup: "Free annual health check-up provided by the insurer.",
      claim_settlement_ratio: "% of claims approved. HIGHER is BETTER (90%+ is good).",
      network_hospitals: "Hospitals where you get CASHLESS treatment. More hospitals = better access."
    };

    // Rider explanations
    const riderExplanations = {
      "Unlimited Restore": "Sum Insured restored UNLIMITED times per year after each claim.",
      "Hospital Cash Benefit": "Fixed daily cash (Rs.500-5000/day) for personal expenses during hospitalization.",
      "Critical Illness Cover": "Lump sum payout on diagnosis of serious illnesses (cancer, heart attack, stroke).",
      "Parenthood": "Maternity coverage - pregnancy, delivery, newborn baby expenses. Usually 9-month waiting.",
      "Optima Wellbeing": "OPD and wellness benefits - doctor consultations, health checkups, gym membership.",
      "ABCD Chronic Care": "Coverage for chronic conditions like diabetes, asthma, BP from Day 1.",
      "Limitless": "Removes all limits on Sum Insured - infinite coverage for major claims.",
      "Consumables Cover": "Covers all consumables (gloves, syringes, PPE) which can cost Rs.20,000+.",
      "NCB Super": "Enhanced No Claim Bonus - higher percentage increase each claim-free year.",
      "Claim Protect": "Covers all non-medical expenses during hospitalization (food, travel, etc.).",
      "Air Ambulance": "Emergency helicopter ambulance for remote areas - up to Rs.10 Lakhs.",
      "PED Reduction": "Reduces waiting period for pre-existing diseases from 4 years to 1-2 years.",
      "Maternity Cover": "Covers normal/C-section delivery, pre/post natal care, newborn expenses.",
      "OPD Cover": "Covers outpatient expenses - doctor visits without hospitalization.",
      "Personal Accident": "Additional coverage for accidental death and disability.",
      "Cancer Care": "Enhanced coverage specifically for cancer treatment and related expenses.",
      "Second E-Opinion": "Get expert opinion from top doctors for serious illness diagnosis.",
      "Wellness Rewards": "Earn rewards/cashback for healthy lifestyle activities."
    };

    // Helper: Wrap text
    const wrapText = (text, maxWidth, fontSize) => {
      if (!text) return ["N/A"];
      const words = String(text).split(" ");
      const lines = [];
      let currentLine = "";
      pdf.setFontSize(fontSize);
      words.forEach(word => {
        const testLine = currentLine + (currentLine ? " " : "") + word;
        const testWidth = pdf.getTextWidth(testLine);
        if (testWidth > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });
      if (currentLine) lines.push(currentLine);
      return lines.length > 0 ? lines : ["N/A"];
    };

    // Helper: Check page break
    const checkPageBreak = (requiredHeight) => {
      if (yPos + requiredHeight > pageHeight - 25) {
        // Add footer
        pdf.setFillColor(...colors.headerBg);
        pdf.rect(0, pageHeight - 12, pageWidth, 12, "F");
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(8);
        pdf.text(`Page ${pageNum} | Kavach Box by Prashant K Uunadkat | +91 9510020907`, pageWidth / 2, pageHeight - 5, { align: "center" });
        
        pdf.addPage();
        pageNum++;
        yPos = 15;
        
        // Mini header on new page
        pdf.setFillColor(...colors.headerBg);
        pdf.rect(0, 0, pageWidth, 10, "F");
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "bold");
        pdf.text(`Health Insurance Quote for ${clientName} (Continued)`, pageWidth / 2, 7, { align: "center" });
        yPos = 15;
        return true;
      }
      return false;
    };

    // ==================== PAGE 1: TITLE & CLIENT INFO ====================
    
    // Header background
    pdf.setFillColor(...colors.headerBg);
    pdf.rect(0, 0, pageWidth, 55, "F");
    
    // Gold accent line
    pdf.setFillColor(...colors.accentGold);
    pdf.rect(0, 55, pageWidth, 3, "F");
    
    // Main title
    pdf.setTextColor(...colors.white);
    pdf.setFontSize(26);
    pdf.setFont("helvetica", "bold");
    pdf.text("Health Insurance Quotation", pageWidth / 2, 22, { align: "center" });
    
    // Subtitle
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");
    pdf.text("Personalized Plan Comparison & Premium Details", pageWidth / 2, 32, { align: "center" });
    
    // Branding
    pdf.setFontSize(10);
    pdf.text("Kavach Box by Prashant K Uunadkat", pageWidth / 2, 42, { align: "center" });
    pdf.setFontSize(8);
    pdf.text(`Generated: ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`, pageWidth / 2, 50, { align: "center" });
    
    yPos = 65;
    
    // Client Info Box
    pdf.setFillColor(...colors.lightGray);
    pdf.rect(margin, yPos, contentWidth, 20, "F");
    pdf.setFillColor(...colors.accentGold);
    pdf.rect(margin, yPos, 4, 20, "F");
    
    pdf.setTextColor(...colors.textDark);
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text(`Prepared For: ${clientName}`, margin + 10, yPos + 8);
    
    pdf.setTextColor(...colors.textMedium);
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    const clientDetails = [];
    if (clientAge) clientDetails.push(`Age: ${clientAge} years`);
    if (clientPhone) clientDetails.push(`Phone: ${clientPhone}`);
    clientDetails.push(`Quote Date: ${new Date(quoteDate).toLocaleDateString("en-IN")}`);
    pdf.text(clientDetails.join("  |  "), margin + 10, yPos + 15);
    
    yPos += 28;
    
    // Plan Cards Overview
    pdf.setTextColor(...colors.textDark);
    pdf.setFontSize(11);
    pdf.setFont("helvetica", "bold");
    pdf.text(`Plans Being Compared (${selectedPlans.length})`, margin, yPos);
    yPos += 8;
    
    const cardWidth = (contentWidth - (selectedPlans.length - 1) * 5) / selectedPlans.length;
    const planColors = [colors.plan1, colors.plan2, colors.plan3, colors.plan4, colors.plan5];
    
    selectedPlans.forEach((plan, idx) => {
      const xPos = margin + idx * (cardWidth + 5);
      
      // Card background
      pdf.setFillColor(...colors.lightGray);
      pdf.rect(xPos, yPos, cardWidth, 28, "F");
      
      // Colored top border
      pdf.setFillColor(...planColors[idx % planColors.length]);
      pdf.rect(xPos, yPos, cardWidth, 4, "F");
      
      // Plan name
      pdf.setTextColor(...colors.textDark);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      const planNameLines = wrapText(plan.plan_name, cardWidth - 6, 9);
      planNameLines.slice(0, 2).forEach((line, i) => {
        pdf.text(line, xPos + 3, yPos + 10 + (i * 4));
      });
      
      // Company name
      pdf.setTextColor(...colors.textMedium);
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");
      const companyLines = wrapText(plan.company || "", cardWidth - 6, 7);
      pdf.text(companyLines[0], xPos + 3, yPos + 20);
      
      // SI count
      pdf.setTextColor(...colors.neutral);
      pdf.setFontSize(6);
      pdf.text(`${(plan.selected_si_options || []).length} SI options | ${(plan.selected_riders || []).length} riders`, xPos + 3, yPos + 25);
    });
    
    yPos += 36;
    
    const colWidth = (contentWidth - 55) / selectedPlans.length;
    
    // ==================== SECTION: FEATURE COMPARISON ====================
    
    const addSection = (title, features, sectionExplanation = "") => {
      checkPageBreak(25);
      
      // Section header
      pdf.setFillColor(...colors.sectionBg);
      pdf.rect(margin, yPos, contentWidth, 14, "F");
      pdf.setFillColor(...colors.accentGold);
      pdf.rect(margin, yPos, 4, 14, "F");
      
      pdf.setTextColor(...colors.textDark);
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text(title, margin + 10, yPos + 9);
      
      yPos += 16;
      
      // Section explanation
      if (sectionExplanation) {
        pdf.setTextColor(...colors.textMedium);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "italic");
        const expLines = wrapText(sectionExplanation, contentWidth - 10, 8);
        expLines.forEach(line => {
          pdf.text(line, margin + 5, yPos);
          yPos += 4;
        });
        yPos += 3;
      }
      
      // Column headers
      pdf.setFillColor(...colors.headerBg);
      pdf.rect(margin, yPos, contentWidth, 10, "F");
      pdf.setTextColor(...colors.white);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");
      pdf.text("Feature / Benefit", margin + 3, yPos + 6.5);
      
      selectedPlans.forEach((plan, idx) => {
        const xPos = margin + 55 + (idx * colWidth);
        const shortName = plan.plan_name.length > 15 ? plan.plan_name.substring(0, 13) + ".." : plan.plan_name;
        pdf.text(shortName, xPos + 2, yPos + 6.5);
      });
      yPos += 12;
      
      // Feature rows with explanations
      let rowIdx = 0;
      features.forEach(({ key, label }) => {
        const explanation = featureExplanations[key] || "";
        const hasExplanation = explanation.length > 0;
        const rowHeight = hasExplanation ? 16 : 10;
        
        checkPageBreak(rowHeight + 5);
        
        // Alternating row background
        const bgColor = rowIdx % 2 === 0 ? colors.white : colors.lightGray;
        pdf.setFillColor(...bgColor);
        pdf.rect(margin, yPos, contentWidth, rowHeight, "F");
        
        // Feature label (bold)
        pdf.setTextColor(...colors.textDark);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "bold");
        pdf.text(label, margin + 3, yPos + 5);
        
        // Feature explanation (small, italic)
        if (hasExplanation) {
          pdf.setTextColor(...colors.textLight);
          pdf.setFontSize(6);
          pdf.setFont("helvetica", "italic");
          const expLines = wrapText(explanation, 50, 6);
          expLines.slice(0, 2).forEach((line, i) => {
            pdf.text(line, margin + 3, yPos + 9 + (i * 3));
          });
        }
        
        // Values for each plan
        selectedPlans.forEach((plan, idx) => {
          const xPos = margin + 55 + (idx * colWidth);
          let value = plan.features?.[key] || "N/A";
          if (typeof value === "object") value = JSON.stringify(value);
          value = String(value);
          
          // Color code values
          const lowerValue = value.toLowerCase();
          if (lowerValue.includes("n/a") || lowerValue === "not covered" || lowerValue === "not available") {
            pdf.setTextColor(...colors.negative);
          } else if (lowerValue.includes("yes") || lowerValue.includes("covered") || lowerValue.includes("no capping") || lowerValue.includes("nil") || lowerValue === "0%" || lowerValue.includes("no limits")) {
            pdf.setTextColor(...colors.positive);
          } else {
            pdf.setTextColor(...colors.textDark);
          }
          
          pdf.setFontSize(8);
          pdf.setFont("helvetica", "normal");
          const valueLines = wrapText(value, colWidth - 4, 8);
          valueLines.slice(0, 2).forEach((line, i) => {
            pdf.text(line, xPos + 2, yPos + 5 + (i * 4));
          });
        });
        
        yPos += rowHeight;
        rowIdx++;
      });
      
      yPos += 6;
    };
    
    // ==================== ALL SECTIONS ====================
    
    // 1. CLAIM EXPERIENCE
    addSection(
      "CLAIM EXPERIENCE (IRDAI Data)",
      [
        { key: "claim_settlement_ratio", label: "Claim Settlement Ratio" },
        { key: "network_hospitals", label: "Network Hospitals" }
      ],
      "Official regulatory data. Higher CSR = more claims approved. More hospitals = better access to cashless treatment."
    );
    
    // 2. ROOM & HOSPITALIZATION
    addSection(
      "ROOM & HOSPITALIZATION",
      [
        { key: "room_rent", label: "Room Rent Coverage" },
        { key: "icu_charges", label: "ICU Charges" },
        { key: "pre_hospitalization", label: "Pre-Hospitalization" },
        { key: "post_hospitalization", label: "Post-Hospitalization" },
        { key: "day_care", label: "Day Care Procedures" },
        { key: "domiciliary", label: "Domiciliary Treatment" }
      ],
      "Coverage for hospital stays and related treatments. 'No capping' on room rent is BEST."
    );
    
    // 3. ADVANCED TREATMENTS
    addSection(
      "ADVANCED TREATMENTS",
      [
        { key: "modern_treatments", label: "Modern Treatments" },
        { key: "organ_donor", label: "Organ Donor Cover" }
      ],
      "Coverage for advanced medical procedures and transplants."
    );
    
    // 4. WAITING PERIODS
    addSection(
      "WAITING PERIODS (Shorter is Better)",
      [
        { key: "ped_waiting", label: "Pre-Existing Disease (PED)" },
        { key: "specific_disease_waiting", label: "Specific Disease Waiting" }
      ],
      "SHORTER waiting periods are BETTER for you. During this time, related claims won't be covered."
    );
    
    // 5. BONUS & RESTORE
    addSection(
      "BONUS & RESTORE BENEFITS",
      [
        { key: "restore_benefit", label: "Restore Benefit" },
        { key: "ncb", label: "No Claim Bonus (NCB)" }
      ],
      "These benefits INCREASE your coverage over time. Very important for long-term value."
    );
    
    // 6. CO-PAYMENT & CONSUMABLES
    addSection(
      "CO-PAYMENT & EXTRAS",
      [
        { key: "copay", label: "Co-Payment" },
        { key: "consumables", label: "Consumables Cover" },
        { key: "health_checkup", label: "Health Checkup" }
      ],
      "Co-pay = what YOU pay from pocket. NIL/0% is BEST. Consumables can add Rs.20,000+ to hospital bills."
    );
    
    // ==================== RIDERS SECTION ====================
    checkPageBreak(50);
    
    pdf.setFillColor(...colors.sectionBg);
    pdf.rect(margin, yPos, contentWidth, 14, "F");
    pdf.setFillColor(...colors.accentGold);
    pdf.rect(margin, yPos, 4, 14, "F");
    
    pdf.setTextColor(...colors.textDark);
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text("RIDERS SELECTED FOR THIS QUOTE", margin + 10, yPos + 9);
    yPos += 16;
    
    pdf.setTextColor(...colors.textMedium);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "italic");
    pdf.text("Riders are OPTIONAL add-ons that enhance your policy. Each rider has additional premium.", margin + 5, yPos);
    yPos += 8;
    
    selectedPlans.forEach((plan, planIdx) => {
      checkPageBreak(30);
      
      // Plan header for riders
      pdf.setFillColor(...planColors[planIdx % planColors.length]);
      pdf.rect(margin, yPos, contentWidth, 8, "F");
      pdf.setTextColor(...colors.white);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.text(`${plan.plan_name} - ${plan.company}`, margin + 5, yPos + 5.5);
      yPos += 10;
      
      const selectedRiders = plan.selected_riders || [];
      if (selectedRiders.length === 0) {
        pdf.setFillColor(...colors.lightGray);
        pdf.rect(margin, yPos, contentWidth, 8, "F");
        pdf.setTextColor(...colors.textMedium);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "italic");
        pdf.text("No riders selected for this plan", margin + 5, yPos + 5.5);
        yPos += 10;
      } else {
        selectedRiders.forEach((riderName, rIdx) => {
          checkPageBreak(16);
          
          const bgColor = rIdx % 2 === 0 ? colors.white : colors.lightGray;
          pdf.setFillColor(...bgColor);
          pdf.rect(margin, yPos, contentWidth, 14, "F");
          
          // Checkmark
          pdf.setTextColor(...colors.positive);
          pdf.setFontSize(10);
          pdf.setFont("helvetica", "bold");
          pdf.text("[+]", margin + 3, yPos + 6);
          
          // Rider name
          pdf.setTextColor(...colors.textDark);
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "bold");
          pdf.text(riderName, margin + 12, yPos + 6);
          
          // Rider explanation
          const riderExp = riderExplanations[riderName] || plan.available_riders?.find(r => r.name === riderName)?.description || "";
          if (riderExp) {
            pdf.setTextColor(...colors.textMedium);
            pdf.setFontSize(7);
            pdf.setFont("helvetica", "normal");
            const expLines = wrapText(riderExp, contentWidth - 20, 7);
            pdf.text(expLines[0], margin + 12, yPos + 11);
          }
          
          yPos += 14;
        });
      }
      yPos += 5;
    });
    
    // ==================== PREMIUM SECTION ====================
    checkPageBreak(40);
    
    pdf.setFillColor(...colors.headerBg);
    pdf.rect(margin, yPos, contentWidth, 14, "F");
    pdf.setFillColor(...colors.accentGold);
    pdf.rect(margin, yPos, 4, 14, "F");
    
    pdf.setTextColor(...colors.white);
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text("PREMIUM DETAILS", margin + 10, yPos + 9);
    yPos += 18;
    
    pdf.setTextColor(...colors.textMedium);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "italic");
    pdf.text("Premiums shown are INDICATIVE. Final premium depends on health conditions and underwriting.", margin + 5, yPos);
    yPos += 8;
    
    // Premium table for each plan
    selectedPlans.forEach((plan, planIdx) => {
      checkPageBreak(40);
      
      // Plan header
      pdf.setFillColor(...planColors[planIdx % planColors.length]);
      pdf.rect(margin, yPos, contentWidth, 8, "F");
      pdf.setTextColor(...colors.white);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.text(`${plan.plan_name}`, margin + 5, yPos + 5.5);
      yPos += 10;
      
      // Premium table headers
      pdf.setFillColor(...colors.sectionBg);
      pdf.rect(margin, yPos, contentWidth, 8, "F");
      pdf.setTextColor(...colors.textDark);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");
      pdf.text("Sum Insured", margin + 3, yPos + 5.5);
      
      const premColWidth = (contentWidth - 40) / 4;
      PREMIUM_TERMS.forEach((term, tIdx) => {
        pdf.text(term.label.split(" ")[0], margin + 40 + (tIdx * premColWidth), yPos + 5.5);
      });
      yPos += 10;
      
      // Premium rows for each SI
      const siOptions = plan.selected_si_options || [];
      siOptions.forEach((si, siIdx) => {
        checkPageBreak(10);
        
        const bgColor = siIdx % 2 === 0 ? colors.white : colors.lightGray;
        pdf.setFillColor(...bgColor);
        pdf.rect(margin, yPos, contentWidth, 8, "F");
        
        pdf.setTextColor(...colors.textDark);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "bold");
        pdf.text(`Rs. ${si}`, margin + 3, yPos + 5.5);
        
        pdf.setFont("helvetica", "normal");
        const premiums = plan.premiums?.[si] || {};
        PREMIUM_TERMS.forEach((term, tIdx) => {
          const premium = premiums[term.id];
          if (premium) {
            pdf.setTextColor(...colors.positive);
            pdf.text(`Rs. ${premium}`, margin + 40 + (tIdx * premColWidth), yPos + 5.5);
          } else {
            pdf.setTextColor(...colors.textLight);
            pdf.text("-", margin + 40 + (tIdx * premColWidth), yPos + 5.5);
          }
        });
        
        yPos += 8;
      });
      
      yPos += 8;
    });
    
    // ==================== FOOTER / DISCLAIMER ====================
    checkPageBreak(35);
    
    pdf.setFillColor(...colors.lightGray);
    pdf.rect(margin, yPos, contentWidth, 28, "F");
    
    pdf.setTextColor(...colors.textDark);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("Important Notes:", margin + 5, yPos + 6);
    
    pdf.setTextColor(...colors.textMedium);
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "normal");
    pdf.text("1. Premiums shown are indicative. Insurers may apply loading based on health conditions and medical history.", margin + 5, yPos + 12);
    pdf.text("2. Policy issuance is subject to medical underwriting and acceptance by the insurance company.", margin + 5, yPos + 17);
    pdf.text("3. This quote is valid for 15 days from the date of generation. Terms may change thereafter.", margin + 5, yPos + 22);
    
    // Contact info
    pdf.setFillColor(...colors.headerBg);
    pdf.rect(margin, yPos + 28, contentWidth, 12, "F");
    pdf.setTextColor(...colors.white);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("Need Help? Contact: +91 9510020907 | WhatsApp: +91 9979860107", pageWidth / 2, yPos + 35, { align: "center" });
    
    // Final page footer
    pdf.setFillColor(...colors.headerBg);
    pdf.rect(0, pageHeight - 12, pageWidth, 12, "F");
    pdf.setTextColor(...colors.white);
    pdf.setFontSize(8);
    pdf.text(`Page ${pageNum} | Kavach Box by Prashant K Uunadkat | Your Trusted Insurance Advisor`, pageWidth / 2, pageHeight - 5, { align: "center" });

    // Save PDF
    const fileName = `Quote_${clientName.replace(/\s+/g, "_")}_${quoteDate}.pdf`;
    pdf.save(fileName);
  };

  const togglePlanExpand = (planId) => {
    setExpandedPlans(prev => ({ ...prev, [planId]: !prev[planId] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F6FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D4A017]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6FA]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#C9D4E6] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/advisor/dashboard")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-[#1B2A4A] flex items-center gap-2">
                  <FileText className="h-6 w-6 text-[#A87608]" />
                  {isEditing ? "Edit Quote" : "Create New Quote"}
                </h1>
                <p className="text-sm text-[#3A4557]">Generate comparison quote for your client</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => saveQuote("draft")}
                disabled={saving}
                variant="outline"
                className="border-[#C9D4E6] text-[#2E3B57]"
                data-testid="save-draft-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button
                onClick={saveAndShare}
                disabled={saving}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold"
                data-testid="share-link-btn"
              >
                {linkCopied ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Link Copied!
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4 mr-2" />
                    Share Link
                  </>
                )}
              </Button>
              {savedQuoteId && (
                <Button
                  onClick={openShareableLink}
                  variant="outline"
                  className="border-[#C9D4E6] text-[#2E3B57]"
                  data-testid="preview-link-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              )}
              <Button
                onClick={generatePDF}
                variant="outline"
                className="border-[#C9D4E6] text-[#2E3B57]"
                data-testid="generate-pdf-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Client Details */}
        <Card className="bg-white/50 border-[#C9D4E6]">
          <CardHeader className="pb-4">
            <CardTitle className="text-[#1B2A4A] flex items-center gap-2">
              <User className="h-5 w-5 text-[#A87608]" />
              Client Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-sm text-[#3A4557] block mb-1">Client Name *</label>
                <Input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Enter client name"
                  className="bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A]"
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#3A4557] block mb-1">Age (Seniormost)</label>
                <Input
                  value={clientAge}
                  onChange={(e) => setClientAge(e.target.value)}
                  placeholder="e.g., 35"
                  className="bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A]"
                />
              </div>
              <div>
                <label className="text-sm text-[#3A4557] block mb-1">Phone</label>
                <Input
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="Phone number"
                  className="bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A]"
                />
              </div>
              <div>
                <label className="text-sm text-[#3A4557] block mb-1">Quote Date</label>
                <DatePicker
                  date={quoteDate}
                  onDateChange={(date) => setQuoteDate(date)}
                  placeholder="Select quote date"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selected Plans */}
        <Card className="bg-white/50 border-[#C9D4E6]">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[#1B2A4A] flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#A87608]" />
                Selected Plans ({selectedPlans.length})
              </CardTitle>
              <Button
                onClick={() => setShowPlanSelector(true)}
                className="bg-[#D4A017] hover:bg-[#D4A017] text-black"
                data-testid="add-plan-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Plan
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {selectedPlans.length === 0 ? (
              <div className="text-center py-10 text-[#3A4557]">
                <Shield className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No plans selected. Click &quot;Add Plan&quot; to start.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedPlans.map((plan, idx) => (
                  <div key={plan.plan_id} className="border border-[#C9D4E6] rounded-lg overflow-hidden">
                    {/* Plan Header */}
                    <div
                      className="flex items-center justify-between p-4 bg-[#EDF1F8]/50 cursor-pointer"
                      onClick={() => togglePlanExpand(plan.plan_id)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedPlans[plan.plan_id] ? (
                          <ChevronDown className="h-5 w-5 text-[#3A4557]" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-[#3A4557]" />
                        )}
                        <div>
                          <h3 className="font-semibold text-[#1B2A4A]">{plan.plan_name}</h3>
                          <p className="text-sm text-[#3A4557]">{plan.company}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#3A4557]">
                          {plan.selected_si_options?.length || 0} SI | {plan.selected_riders?.length || 0} Riders
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removePlan(plan.plan_id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Plan Details (Expanded) */}
                    {expandedPlans[plan.plan_id] && (
                      <div className="p-4 space-y-4 border-t border-[#C9D4E6]">
                        {/* Sum Insured Selection */}
                        <div>
                          <label className="text-sm text-[#3A4557] block mb-2">Select Sum Insured Options</label>
                          <div className="flex flex-wrap gap-2">
                            {SI_OPTIONS.map(si => (
                              <button
                                key={si}
                                onClick={() => toggleSIOption(plan.plan_id, si)}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                  plan.selected_si_options?.includes(si)
                                    ? "bg-[#D4A017] text-black"
                                    : "bg-[#EDF1F8] text-[#2E3B57] hover:bg-[#E2E9F4]"
                                }`}
                              >
                                ₹{si}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Premium Entry for Each SI */}
                        {(plan.selected_si_options || []).length > 0 && (
                          <div>
                            <label className="text-sm text-[#3A4557] block mb-2">
                              <DollarSign className="h-4 w-4 inline mr-1" />
                              Enter Premiums
                            </label>
                            <div className="space-y-3">
                              {(plan.selected_si_options || []).map(si => (
                                <div key={si} className="bg-[#EDF1F8]/30 rounded-lg p-3">
                                  <p className="text-sm font-medium text-[#1B2A4A] mb-2">Sum Insured: ₹{si}</p>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {PREMIUM_TERMS.map(term => (
                                      <div key={term.id}>
                                        <label className="text-xs text-[#3A4557]">{term.label}</label>
                                        <Input
                                          type="text"
                                          value={plan.premiums?.[si]?.[term.id] || ""}
                                          onChange={(e) => updatePremium(plan.plan_id, si, term.id, e.target.value)}
                                          placeholder="₹"
                                          className="bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] h-8 text-sm"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Rider Selection */}
                        {(plan.available_riders || []).length > 0 && (
                          <div>
                            <label className="text-sm text-[#3A4557] block mb-2">
                              <Sparkles className="h-4 w-4 inline mr-1" />
                              Select Riders (Available: {plan.available_riders.length})
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {plan.available_riders.map(rider => (
                                <button
                                  key={rider.name}
                                  onClick={() => toggleRider(plan.plan_id, rider.name)}
                                  className={`p-3 rounded-lg text-left transition-colors ${
                                    plan.selected_riders?.includes(rider.name)
                                      ? "bg-green-500/20 border border-green-500/50"
                                      : "bg-[#EDF1F8]/50 border border-[#C9D4E6] hover:border-[#C9D4E6]"
                                  }`}
                                >
                                  <div className="flex items-start gap-2">
                                    <div className={`mt-0.5 h-4 w-4 rounded flex items-center justify-center flex-shrink-0 ${
                                      plan.selected_riders?.includes(rider.name)
                                        ? "bg-green-500 text-white"
                                        : "bg-[#E2E9F4]"
                                    }`}>
                                      {plan.selected_riders?.includes(rider.name) && <Check className="h-3 w-3" />}
                                    </div>
                                    <div>
                                      <p className={`text-sm font-medium ${
                                        plan.selected_riders?.includes(rider.name) ? "text-green-600" : "text-[#1B2A4A]"
                                      }`}>
                                        {rider.name}
                                      </p>
                                      {rider.description && (
                                        <p className="text-xs text-[#3A4557] mt-0.5">{rider.description}</p>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="bg-white/50 border-[#C9D4E6]">
          <CardHeader className="pb-4">
            <CardTitle className="text-[#1B2A4A] text-sm">Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes for this quote..."
              rows={3}
              className="w-full bg-[#EDF1F8] border border-[#C9D4E6] text-[#1B2A4A] rounded-md px-3 py-2 resize-none"
            />
          </CardContent>
        </Card>

        {/* Know Your Rights Section */}
        <Card className="bg-gradient-to-br from-blue-900/30 to-[#EDF1F8]/50 border-blue-500/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-[#1B2A4A] flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#2E3B57]" />
              Know Your Rights as a Policyholder
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-[#EDF1F8]/50 rounded-lg border border-[#C9D4E6]/50">
                <h4 className="font-semibold text-[#A87608] mb-2 flex items-center gap-2">
                  <span className="h-6 w-6 rounded-full bg-[#D4A017]/15 flex items-center justify-center text-xs">1</span>
                  Free Look Period
                </h4>
                <p className="text-sm text-[#2E3B57]">
                  You have <span className="text-[#1B2A4A] font-medium">15-30 days</span> to review your policy after purchase. 
                  If not satisfied, you can return it for a full refund (minus stamp duty and medical expenses).
                </p>
              </div>
              
              <div className="p-4 bg-[#EDF1F8]/50 rounded-lg border border-[#C9D4E6]/50">
                <h4 className="font-semibold text-[#A87608] mb-2 flex items-center gap-2">
                  <span className="h-6 w-6 rounded-full bg-[#D4A017]/15 flex items-center justify-center text-xs">2</span>
                  Portability Right
                </h4>
                <p className="text-sm text-[#2E3B57]">
                  You can <span className="text-[#1B2A4A] font-medium">switch insurers</span> while retaining waiting period credits. 
                  Apply 45 days before renewal to port your policy.
                </p>
              </div>
              
              <div className="p-4 bg-[#EDF1F8]/50 rounded-lg border border-[#C9D4E6]/50">
                <h4 className="font-semibold text-[#A87608] mb-2 flex items-center gap-2">
                  <span className="h-6 w-6 rounded-full bg-[#D4A017]/15 flex items-center justify-center text-xs">3</span>
                  Cashless Treatment
                </h4>
                <p className="text-sm text-[#2E3B57]">
                  At network hospitals, the insurer pays directly. For emergencies, 
                  <span className="text-[#1B2A4A] font-medium"> pre-authorization within 1 hour</span> is mandatory by IRDAI.
                </p>
              </div>
              
              <div className="p-4 bg-[#EDF1F8]/50 rounded-lg border border-[#C9D4E6]/50">
                <h4 className="font-semibold text-[#A87608] mb-2 flex items-center gap-2">
                  <span className="h-6 w-6 rounded-full bg-[#D4A017]/15 flex items-center justify-center text-xs">4</span>
                  Claim Settlement Timeline
                </h4>
                <p className="text-sm text-[#2E3B57]">
                  Insurers must settle claims within <span className="text-[#1B2A4A] font-medium">30 days</span> of receiving 
                  all documents. You can escalate to IRDAI if delayed.
                </p>
              </div>
              
              <div className="p-4 bg-[#EDF1F8]/50 rounded-lg border border-[#C9D4E6]/50">
                <h4 className="font-semibold text-[#A87608] mb-2 flex items-center gap-2">
                  <span className="h-6 w-6 rounded-full bg-[#D4A017]/15 flex items-center justify-center text-xs">5</span>
                  Grievance Redressal
                </h4>
                <p className="text-sm text-[#2E3B57]">
                  If claim is rejected unfairly, file complaint with <span className="text-[#1B2A4A] font-medium">IRDAI IGMS portal</span> or 
                  Insurance Ombudsman within 1 year.
                </p>
              </div>
              
              <div className="p-4 bg-[#EDF1F8]/50 rounded-lg border border-[#C9D4E6]/50">
                <h4 className="font-semibold text-[#A87608] mb-2 flex items-center gap-2">
                  <span className="h-6 w-6 rounded-full bg-[#D4A017]/15 flex items-center justify-center text-xs">6</span>
                  Lifelong Renewability
                </h4>
                <p className="text-sm text-[#2E3B57]">
                  IRDAI mandates <span className="text-[#1B2A4A] font-medium">lifetime renewal</span> for health policies. 
                  Insurer cannot refuse renewal based on claims or age.
                </p>
              </div>
            </div>
            
            <div className="p-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-lg">
              <p className="text-sm text-[#8F6A06]">
                <strong>💡 Tip:</strong> Always read the policy wording document carefully. 
                For any queries, contact your advisor or IRDAI toll-free at <span className="text-[#1B2A4A] font-medium">155255</span>.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Plan Selector Modal */}
      {showPlanSelector && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-[#C9D4E6] rounded-xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#C9D4E6]">
              <h2 className="text-lg font-bold text-[#1B2A4A]">Select Plan to Add</h2>
              <button onClick={() => setShowPlanSelector(false)} className="text-[#3A4557] hover:text-[#1B2A4A]">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availablePlans.map(plan => {
                  const isAdded = selectedPlans.find(p => p.plan_id === plan.id);
                  return (
                    <button
                      key={plan.id}
                      onClick={() => !isAdded && addPlan(plan)}
                      disabled={isAdded}
                      className={`p-4 rounded-lg text-left transition-colors ${
                        isAdded
                          ? "bg-[#EDF1F8]/30 opacity-50 cursor-not-allowed"
                          : "bg-[#EDF1F8]/50 hover:bg-[#EDF1F8] border border-[#C9D4E6] hover:border-[#D4A017]/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-[#E2E9F4] flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-[#3A4557]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#1B2A4A]">{plan.name}</p>
                          <p className="text-sm text-[#3A4557]">{plan.company}</p>
                        </div>
                        {isAdded && (
                          <span className="ml-auto text-xs text-green-600 bg-green-500/20 px-2 py-1 rounded">
                            Added
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
